import { useCallback, useRef } from 'react';

/**
 * Hook that provides a function to announce messages to screen readers
 * via the shared aria-live regions mounted by `<LiveRegion />`.
 *
 * Uses the double-clear pattern to ensure repeated identical messages
 * are announced each time.
 */
export function useAnnounce() {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const region = document.querySelector<HTMLElement>(`[data-live-region="${priority}"]`);
    if (!region) return;

    // Clear any pending announcement
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Double-clear pattern: set empty first, then set message in next tick
    // This ensures screen readers announce even if the message is the same as before
    region.textContent = '';
    timeoutRef.current = setTimeout(() => {
      region.textContent = message;
    }, 100);
  }, []);

  return announce;
}
